import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import ApiClient from '../../../api/client'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useFamilyStore } from '../../../../store/useFamilyStore'

const apiClient = new ApiClient()

const UpdateMemberForm = ({ onSuccess }) => {
  const { closeUpdateModal, showUpdateModal, updateMember } = useFamilyStore()

  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(true)

  // Form fields
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [relationship, setRelationship] = useState('')
  const [birthDate, setBirthDate] = useState('')

  // Image handling
  const [uploadedImage, setUploadedImage] = useState(null)
  const [selectedAvatar, setSelectedAvatar] = useState('')
  const [imagePreview, setImagePreview] = useState(null)

  // Choices from API
  const [relationshipChoices, setRelationshipChoices] = useState([])

  // Avatar URLs
  const avatarChoices = [
    'https://c.animaapp.com/etPela4A/img/profile-6@2x.png',
    'https://c.animaapp.com/etPela4A/img/image-1@2x.png',
    'https://c.animaapp.com/etPela4A/img/image-2@2x.png',
    'https://c.animaapp.com/etPela4A/img/image-3@2x.png',
  ]

  // Fetch relationship choices on component mount
  useEffect(() => {
    if (updateMember) {
      setName(updateMember.full_name)
      setEmail(updateMember.email || '')
      setUploadedImage(null)
      setImagePreview(updateMember.image || null)
      setRelationship(updateMember.relationship || '')
      setBirthDate(updateMember.birth_date || '')
    }

    const fetchRelationshipChoices = async () => {
      try {
        const result = await apiClient.getRelationshipChoices()
        if (result.data) {
          setRelationshipChoices(result.data)
        }
      } catch (error) {
        console.error('Error fetching relationship choices:', error)
        // Use default choices if API fails
        setRelationshipChoices([
          { value: 'primary applicant', label: 'Primary Applicant' },
          { value: 'spouse', label: 'Spouse' },
          { value: 'child', label: 'Child' },
          { value: 'relative', label: 'Relative' },
          { value: 'sibling', label: 'Sibling' },
          { value: 'relative', label: 'Relative' },
        ])
      } finally {
        setLoading(false)
      }
    }

    fetchRelationshipChoices()
  }, [showUpdateModal])

  // Handle uploaded file
  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setUploadedImage(file)

      // Clear selected avatar when custom image is uploaded
      setSelectedAvatar('')

      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result)
      }
      reader.readAsDataURL(file)

      // Clear error
      if (errors.image) {
        setErrors((prev) => ({ ...prev, image: '' }))
      }
    }
  }

  // Handle avatar selection
  const handleAvatarSelect = (avatarUrl) => {
    setSelectedAvatar(avatarUrl)

    // Clear uploaded image when avatar is selected
    setUploadedImage(null)
    setImagePreview(null)

    // Reset file input
    const fileInput = document.querySelector('input[type="file"]')
    if (fileInput) {
      fileInput.value = ''
    }
  }

  // Clear error when user types
  const handleInputChange = (field, value) => {
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }))
    }
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    if (!name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!relationship.trim()) {
      newErrors.relationship = 'Relationship is required'
    }

    if (!birthDate.trim()) {
      newErrors.birthDate = 'Birth date is required'
    }

    // Email validation (optional field)
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      // Use the correct API call with individual parameters
      const imageToSend = uploadedImage || selectedAvatar
      const result = await apiClient.updateFamilyMember(
        updateMember.id,
        name,
        email,
        imageToSend,
        relationship,
        birthDate
      )

      if (result.error) {
        toast.error('Error updating family member', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'colored',
        })
        console.error('Error updating family member:', result.error)
      } else {
        toast.success('Family member updated successfully', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'colored',
        })
        // onCancel() // Close modal
        closeUpdateModal()
        onSuccess()
      }
    } catch (error) {
      console.error('Unexpected error:', error)
      toast.error('An unexpected error occurred', {
        position: 'top-right',
        autoClose: 3000,
        theme: 'colored',
      })
    }
  }
  if (!showUpdateModal) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold text-black mb-6">
          Update Family Member
        </h2>
        <div className="flex justify-center rounded-full pointer p-2 w-full">
          <img
            className="rounded-full pointer w-20 h-20"
            src={imagePreview}
            alt=""
          />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                // handleInputChange('name', e.target.value)
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d6be0] focus:border-transparent"
              placeholder="Enter full name"
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          {/* Email Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                // handleInputChange('email', e.target.value)
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d6be0] focus:border-transparent"
              placeholder="Enter email address"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          {/* Relationship */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Relationship *
            </label>
            <select
              name="relationship"
              value={relationship}
              onChange={(e) => {
                setRelationship(e.target.value)
                // handleInputChange('relationship', e.target.value)
              }}
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d6be0] focus:border-transparent disabled:bg-gray-100"
            >
              <option value="">
                {loading ? 'Loading relationships...' : 'Select relationship'}
              </option>
              {relationshipChoices.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
            {errors.relationship && (
              <p className="text-red-500 text-sm mt-1">{errors.relationship}</p>
            )}
          </div>

          {/* Profile Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Profile Image
            </label>
            <input
              type="file"
              name="profile_image"
              accept="image/*"
              onChange={handleFileChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d6be0] focus:border-transparent"
            />
            {imagePreview && (
              <div className="mt-3">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-20 h-20 rounded-full object-cover border-2 border-[#3d6be0]"
                />
              </div>
            )}
            {errors.image && (
              <p className="text-red-500 text-sm mt-1">{errors.image}</p>
            )}
          </div>

          {/* Birth Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Birth Date *
            </label>
            <input
              type="date"
              name="birth_date"
              value={birthDate}
              onChange={(e) => {
                setBirthDate(e.target.value)
                // handleInputChange('birthDate', e.target.value)
              }}
              min="1900-01-01"
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d6be0] focus:border-transparent"
            />
            {errors.birthDate && (
              <p className="text-red-500 text-sm mt-1">{errors.birthDate}</p>
            )}
          </div>

          {/* Avatar Selection */}
          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Choose Default Profile Picture
            </label>
            <div className="grid grid-cols-4 gap-2">
              {avatarChoices.map((avatarUrl, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleAvatarSelect(avatarUrl)}
                  className={`relative w-16 h-16 rounded-full overflow-hidden border-2 transition-all ${
                    selectedAvatar === avatarUrl
                      ? 'border-[#3d6be0] ring-2 ring-[#3d6be0] ring-opacity-50 scale-105'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <img
                    src={avatarUrl}
                    alt={`Avatar option ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {selectedAvatar === avatarUrl && (
                    <div className="absolute inset-0 bg-[#3d6be0] bg-opacity-30 flex items-center justify-center">
                      <svg
                        className="w-8 h-8 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Note: If you upload a custom image above, it will override this
              selection
            </p>
          </div> */}

          {/* Form Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={closeUpdateModal}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-[#3d6be0] text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-[#3d6be0] disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Loading...' : 'Update Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

UpdateMemberForm.propTypes = {
  onCancel: PropTypes.func.isRequired,
}

export default UpdateMemberForm
